import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";

export function PlanForm({ plan, catalog, onSave, onCancel, saving }) {
  const [name, setName] = useState(plan?.name || "");
  const [slug, setSlug] = useState(plan?.slug || "");
  const [monthly, setMonthly] = useState(plan?.monthly_price ?? 0);
  const [yearly, setYearly] = useState(plan?.yearly_price ?? 0);
  const [desc, setDesc] = useState(plan?.description || "");
  const [features, setFeatures] = useState(plan?.features?.join("\n") || "");
  const [active, setActive] = useState(plan?.is_active ?? true);
  const [entitlements, setEntitlements] = useState(() => plan?.entitlements || Object.fromEntries(
    catalog.map((feature) => [feature.key, feature.type === "boolean" ? false : (feature.minimum ?? 0)]),
  ));
  const [stripeMonthlyPriceId, setStripeMonthlyPriceId] = useState(plan?.stripe_monthly_price_id || "");
  const [stripeYearlyPriceId, setStripeYearlyPriceId] = useState(plan?.stripe_yearly_price_id || "");

  const groups = catalog.reduce((result, feature) => {
    const group = feature.group || "Features";
    result[group] = [...(result[group] || []), feature];
    return result;
  }, {});

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name, slug: slug || name.toLowerCase().replace(/\s+/g, "-"),
      monthly_price: parseFloat(monthly) || 0,
      yearly_price: parseFloat(yearly) || 0,
      description: desc,
      features: features.split("\n").map((f) => f.trim()).filter(Boolean),
      is_active: active,
      entitlements,
      stripe_monthly_price_id: stripeMonthlyPriceId.trim(),
      stripe_yearly_price_id: stripeYearlyPriceId.trim(),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Plan Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="plan-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Slug</Label>
          <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="auto-generated" data-testid="plan-slug-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Monthly Price ($)</Label>
          <Input type="number" step="0.01" value={monthly} onChange={(e) => setMonthly(e.target.value)} data-testid="plan-monthly-input" />
        </div>
        <div className="space-y-2">
          <Label>Yearly Price ($)</Label>
          <Input type="number" step="0.01" value={yearly} onChange={(e) => setYearly(e.target.value)} data-testid="plan-yearly-input" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Description</Label>
        <Input value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="plan-desc-input" />
      </div>
      <div className="space-y-2">
        <Label>Features (one per line)</Label>
        <p className="text-xs text-muted-foreground">Customer-facing feature list. Actual access is controlled below.</p>
        <Textarea value={features} onChange={(e) => setFeatures(e.target.value)} rows={4} data-testid="plan-features-input" />
      </div>
      <div className="space-y-3 rounded-xl border border-border bg-muted/20 p-4" data-testid="plan-entitlements">
        <div>
          <Label>Product access and limits</Label>
          <p className="text-xs text-muted-foreground mt-1">Changes apply to customers on this plan on their next app refresh. Use -1 for unlimited.</p>
        </div>
        {Object.entries(groups).map(([group, items]) => (
          <div key={group} className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group}</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {items.map((feature) => feature.type === "boolean" ? (
                <div key={feature.key} className="flex items-center justify-between gap-3 rounded-lg border border-border p-2.5">
                  <Label className="text-xs">{feature.label}</Label>
                  <Switch checked={!!entitlements[feature.key]} onCheckedChange={(checked) => setEntitlements((current) => ({ ...current, [feature.key]: checked }))} data-testid={`entitlement-${feature.key}`} />
                </div>
              ) : (
                <div key={feature.key} className="space-y-1 rounded-lg border border-border p-2.5">
                  <Label className="text-xs">{feature.label}</Label>
                  <Input type="number" min="-1" step="1" value={entitlements[feature.key] ?? feature.minimum ?? 0} onChange={(e) => setEntitlements((current) => ({ ...current, [feature.key]: Number.parseInt(e.target.value || "0", 10) }))} data-testid={`entitlement-${feature.key}`} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-3 rounded-xl border border-border p-4">
        <div>
          <Label>Stripe price references (optional)</Label>
          <p className="text-xs text-muted-foreground mt-1">For future catalog synchronization. Current checkout uses the saved prices above.</p>
        </div>
        <Input value={stripeMonthlyPriceId} onChange={(e) => setStripeMonthlyPriceId(e.target.value)} placeholder="Monthly Stripe price ID (price_...)" data-testid="stripe-monthly-price-id" />
        <Input value={stripeYearlyPriceId} onChange={(e) => setStripeYearlyPriceId(e.target.value)} placeholder="Yearly Stripe price ID (price_...)" data-testid="stripe-yearly-price-id" />
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={active} onCheckedChange={setActive} data-testid="plan-active-switch" />
        <Label>Active</Label>
      </div>
      <DialogFooter>
        <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="plan-save-btn">
          {saving ? "Saving..." : "Save Plan"}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function AdminPricing() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [billingHealth, setBillingHealth] = useState(null);

  const loadPlans = async () => {
    try {
      const [plansRes, catalogRes, billingRes] = await Promise.all([
        api.get("/admin/plans"),
        api.get("/admin/plan-feature-catalog"),
        api.get("/admin/billing/health"),
      ]);
      setPlans(plansRes.data || []);
      setCatalog(catalogRes.data || []);
      setBillingHealth(billingRes.data || null);
    } catch {
      toast.error("Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPlans(); }, []);

  const handleCreate = async (data) => {
    setSaving(true);
    try {
      await api.post("/admin/plans", data);
      setShowCreate(false);
      toast.success("Plan created");
      loadPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create plan");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (data) => {
    setSaving(true);
    try {
      await api.put(`/admin/plans/${editPlan.id}`, data);
      setEditPlan(null);
      toast.success("Plan updated");
      loadPlans();
    } catch {
      toast.error("Failed to update plan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm("Delete this plan?")) return;
    try {
      await api.delete(`/admin/plans/${planId}`);
      toast.success("Plan deleted");
      loadPlans();
    } catch {
      toast.error("Failed to delete plan");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">Pricing Plans</h1>
          <p className="text-muted-foreground text-sm">Manage subscription plans</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="add-plan-btn">
          <Plus size={16} className="mr-1" /> Add Plan
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-heading text-lg">Billing health</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ["Stripe-linked users", billingHealth?.stripe_linked_users || 0],
            ["Unprocessed webhooks", billingHealth?.unprocessed_webhooks || 0],
            ["Unmatched payments", billingHealth?.paid_transactions_without_user || 0],
            ["Paid transactions", billingHealth?.transactions?.paid || 0],
          ].map(([label, value]) => (
            <div key={label} className="rounded-lg bg-secondary/40 p-3">
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="text-xl font-bold mt-1">{value}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="plans-grid">
        {plans.map((plan) => (
          <Card key={plan.id} className="card-hover relative">
            {!plan.is_active && (
              <Badge className="absolute top-3 right-3 bg-red-500/10 text-red-400 border-red-500/20 text-xs">Inactive</Badge>
            )}
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <GripVertical size={14} className="text-muted-foreground" />
                {plan.name}
              </CardTitle>
              <p className="text-xs text-muted-foreground font-mono">{plan.slug}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-2xl font-heading font-bold">
                  ${plan.monthly_price}<span className="text-sm font-normal text-muted-foreground">/mo</span>
                </p>
                <p className="text-xs text-muted-foreground">${plan.yearly_price}/year</p>
              </div>
              {plan.description && <p className="text-sm text-muted-foreground">{plan.description}</p>}
              {plan.features?.length > 0 && (
                <ul className="space-y-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="text-xs text-muted-foreground flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-amber-400" /> {f}
                    </li>
                  ))}
                </ul>
              )}
              <div className="text-xs text-muted-foreground">
                {Object.values(plan.entitlements || {}).filter((value) => value === true).length} enabled features · limits configurable
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setEditPlan(plan)} data-testid={`edit-plan-${plan.slug}`}>
                  <Pencil size={12} className="mr-1" /> Edit
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(plan.id)} data-testid={`delete-plan-${plan.slug}`}>
                  <Trash2 size={12} className="mr-1" /> Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Create Plan</DialogTitle></DialogHeader>
          <PlanForm catalog={catalog} onSave={handleCreate} onCancel={() => setShowCreate(false)} saving={saving} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editPlan} onOpenChange={(open) => !open && setEditPlan(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Edit Plan</DialogTitle></DialogHeader>
          {editPlan && <PlanForm plan={editPlan} catalog={catalog} onSave={handleUpdate} onCancel={() => setEditPlan(null)} saving={saving} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
